import { Client } from "@/Common/Api/Client";
import { EmptyProps } from "@/Common/Components/BaseReactComponent";
import { Patient as MyleModelsPatientPatient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

// A ".tsx" file enables JSX support in the TypeScript compiler,
// for more information see the following page on the TypeScript wiki:
// https://github.com/Microsoft/TypeScript/wiki/JSX
class HealthCardScannerState {
    public isReceivingFromScanner: boolean;
    public healthCardNumberFromScanner: string;

    constructor(newState: HealthCardScannerState) {
        this.healthCardNumberFromScanner = newState.healthCardNumberFromScanner;
        this.isReceivingFromScanner = newState.isReceivingFromScanner;
    }
}
/**
 * @export
 * @class HealthCardScanner
 * @description Component will listen for any input from the keyboard. The barcode scanner is essentially a "keyboard".
 *              The barcode scanner (hardware) must be programmed before using with this feature. Usually the manufacture
 *              has a way of programming their barcode and had suffix and prefix to all scanned data. It our case, it will only
 *              work if it receives data with the Pause/Break prefix and suffix. These pre-suf-fix, control the state of the reading.
 *
 *              This component must be at the top level of the application (main.tsx). It will listen for any keyboard event, except if the user
 *              has selected an input box and scans a barcode. The input box will print the code witout the suffix or prefix.
 */
class HealthCardScanner extends React.Component<EmptyProps, HealthCardScannerState> {
    private PREFIX_CHAR_CODE = 124; // F13
    private SUFFIX_CHAR_CODE = 134; // F23
    private PREFIX_KEY = "F13";
    private SUFFIX_KEY = "F23";

    public componentWillUnmount(): void {
        document.removeEventListener("keydown", this.processBarCode);
    }

    public componentWillMount(): void {
        document.addEventListener("keydown", this.processBarCode);
    }

    constructor(props: EmptyProps) {
        super(props);

        this.state = {
            isReceivingFromScanner: false,
            healthCardNumberFromScanner: ""
        };
        this.processBarCode = this.processBarCode.bind(this);
    }

    public render(): JSX.Element {
        return <div />;
    }

    public processBarCode(barcode: KeyboardEvent): void {
        // Detect the prefix char code.
        if (!this.state.isReceivingFromScanner) {
            if (barcode.keyCode == this.PREFIX_CHAR_CODE || barcode.key == this.PREFIX_KEY) {
                // Set the state and start a timeout to see if the user pressed the Pause/Break button by accident.
                // The stream of data from the barcode scanner is very quick, quicker than human hands.
                let s = new HealthCardScannerState(this.state);
                s.isReceivingFromScanner = true;
                this.setState(s);
                window.setTimeout(() => {
                    let s = this.state;
                    if (s.isReceivingFromScanner) {
                        this.setState({ isReceivingFromScanner: false, healthCardNumberFromScanner: "" });
                    }
                }, 500);
            }
        }
        else if (this.state.isReceivingFromScanner) {
            // If we receive the suffix code, we start the process of searching for the user and redirect the app to the patient information page.
            if (barcode.keyCode == this.SUFFIX_CHAR_CODE || barcode.key == this.SUFFIX_KEY) {
                let s = new HealthCardScannerState(this.state);
                s.isReceivingFromScanner = false;
                this.setState(s, () => {
                    let searchPatientData = {
                        Query: this.state.healthCardNumberFromScanner,
                        IsInactive: false,
                        ResultsToReturn: 10,
                        OrderedResponseId: 0,
                        IncludeRelatives: false,
                        IncludePatients: true
                    };
                    // Search for the patient with NAM.
                    // If number of patient found == 1, redirect.
                    // If number of patient found == 0, popup a Patient not found message.
                    // If number of patient found > 1, popup a Too many patients found message.
                    let client = new Client();
                    client.Post(
                        apiSettings.buildApiPath(),
                        ServerOperations.Server.Myle_SearchIndividual,
                        searchPatientData,
                        response => {
                            if (response.SearchIndividualResults.length > 0) {
                                if (response.SearchIndividualResults.length === 1) {
                                    let p = response.SearchIndividualResults[0];
                                    let loadPatientData = { IndividualId: p.ContactId, IncludeRelatedRecalls: true };
                                    let client = new Client();
                                    // Trigger redirect
                                    client.Post(apiSettings.buildApiPath(), ServerOperations.Server.Myle_LoadPatient, loadPatientData, async response => {
                                        var fullpatient = MyleModelsPatientPatient.FromResponseData(response);
                                        await this.loadRamqInfo(fullpatient);
                                        var fullperson = Person.FromPatient(fullpatient);

                                        var operation = new LocalEvent(LocalOperations.Sidebar.Select_Patient);
                                        operation.AddItem("person", fullperson);
                                        LocalEventDispatcher.dispatch(operation);
                                    }, error => console.error(error));
                                }
                                else {
                                    alert("Too many patients found");
                                }
                            }
                            else {
                                alert("Patient not found");
                            }
                        },
                        error => console.error(error)
                    );
                    let s = new HealthCardScannerState(this.state);
                    s.healthCardNumberFromScanner = "";
                    this.setState(s);
                });
            }
            else {
                let keyCode = barcode.keyCode;
                if ((keyCode >= 48 && keyCode <= 57) ||
                    (keyCode >= 65 && keyCode <= 90) ||
                    (keyCode >= 97 && keyCode <= 122)) {
                    let s = new HealthCardScannerState(this.state);
                    s.healthCardNumberFromScanner = s.healthCardNumberFromScanner + barcode.key;
                    this.setState(s);
                }
            }
        }
    }

    private async loadRamqInfo(patient: MyleModelsPatientPatient): Promise<void> {
        const response = await Client.postAsync<RAMQInfo>(
            ServerOperations.Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patient.IndividualResponse.Contact_id
                },
                CurrentUser: sessionStore.getCurrentEmployee()?.getRamqCredentials()
            }
        );

        if (response.isSuccess) {
            const data = new RAMQInfo().FromResponseData(response.data);
            patient.setRamq(data);
        }
    }
}

export { HealthCardScanner };
